import React, { PureComponent } from 'react'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { ReactWaypointProps } from '#src/typings'
import { SellableUrlFragment, DepartmentType } from '#src/generated/graphql'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Grid, GridItem } from '@deal/components'
import { generateGrid } from '#src/services/grid'
import { getSellableEcommerceProperties } from '#src/services/tracking'
import { Experiment } from '@deal/experiment-js'
import { DepartmentSetter } from '#src/context/department'
import uuidv4 from 'uuid/v4'
import { getSellableUrl } from '#src/services/sellable'
import { IdentityConsumer } from '#src/containers/Identity'
import CategoryChatWidget from '#src/containers/CategoryChatWidget'
import CallExpertBanner from '#src/components/CallExpertBanner'
import EngagementChannelHandler from '#src/components/EngagementChannels/EngagementChannelHandler'
import Footer from '#src/components/Footer'
import SellableMetaData from '#src/components/SellableMetaData'
import Amenities from '../../../components/CharterAmenities'
import BackButton from '../../../components/BackButton'
import Description from '../../../components/Description'
import Expert from '../../../components/Expert'
import FAQ from '../../../components/FAQ'
import Features from '../../../components/Features'
import ImageCarousel from '../../../components/ImageCarousel'
import Map from '../../../components/Map'
import YachtPricing from '../../../components/YachtPricing'
import RatesTable from '../../../components/RatesTable'
import SectionHeader from '../../../components/SectionHeader'
import SimilarExperiences from '../../../components/SimilarExperiences'
import Specifications from '../../../components/Specifications'
import Title from '../../../components/Title'
import Waypoint from 'react-waypoint'
import { ExperiencePageProps } from '../../../'
import styles from './styles.css'

interface ExperiencePageState {
  isLazyContentDisplayed: boolean
}

class ExperiencePage extends PureComponent<
  ExperiencePageProps & RouteComponentProps<{}> & WithAnalyticsProps,
  ExperiencePageState
> {
  private activityId?: string

  constructor(props: ExperiencePageProps & RouteComponentProps<{}> & WithAnalyticsProps) {
    super(props)

    this.state = {
      isLazyContentDisplayed: false
    }

    this.handleScroll = this.handleScroll.bind(this)
    this.handleVariationSelected = this.handleVariationSelected.bind(this)
    this.getTrackingProperties = this.getTrackingProperties.bind(this)
  }

  public getTrackingProperties() {
    return Object.assign(
      this.activityId
        ? {
            activity_id: this.activityId
          }
        : {},
      getSellableEcommerceProperties(this.props.sellable)
    )
  }

  public componentDidMount() {
    if (this.props.analytics) {
      this.activityId = uuidv4()

      this.props.analytics.productViewed(this.getTrackingProperties())
    }
  }

  public componentWillUnmount() {
    if (this.props.analytics && this.activityId) {
      this.props.analytics.productViewed(this.getTrackingProperties())
    }
  }

  public componentDidUpdate(prevProps: ExperiencePageProps) {
    if (this.props.analytics && this.props.sellable.id !== prevProps.sellable.id) {
      if (this.activityId) {
        this.props.analytics.productViewEnded(this.getTrackingProperties())
      }

      this.activityId = uuidv4()

      this.props.analytics.productViewed(this.getTrackingProperties())
    }
  }

  public handleVariationSelected(sellable: SellableUrlFragment) {
    this.props.history.replace(getSellableUrl(sellable))
  }

  public handleScroll({ currentPosition }: ReactWaypointProps) {
    this.setState(prevState => {
      return {
        isLazyContentDisplayed: prevState.isLazyContentDisplayed
          ? true
          : currentPosition === 'above'
      }
    })
  }

  public render() {
    const { sellable } = this.props
    const sellableCategory = sellable.categories[0]
    const department = sellableCategory.department ? sellableCategory.department : undefined

    const defaultTopSection = generateGrid([
      {
        title: 2
      },
      {
        features: 2
      },
      {
        pricing: 2
      },
      {
        descriptionHeader: 2
      },
      {
        description: 2
      },
      {
        amenitiesHeader: 2
      },
      {
        amenities: 2
      },
      {
        ratesHeader: 2
      },
      {
        rates: 2
      }
    ])

    const mdTopSection = generateGrid([
      {
        title: 8,
        pricing: 4
      },
      {
        features: 8,
        pricing: 4
      },
      {
        descriptionHeader: 2,
        description: 6,
        pricing: 4
      },
      {
        amenitiesHeader: 2,
        amenities: 6,
        pricing: 4
      },
      {
        ratesHeader: 2,
        rates: 6,
        pricing: 4
      }
    ])

    return (
      <IdentityConsumer>
        {({ myself }) => (
          <>
            <div className={styles.page}>
              {department && <DepartmentSetter department={department} />}
              <SellableMetaData sellable={sellable} />

              <EngagementChannelHandler
                categoryId={sellableCategory.id}
                defaultExperiment={{
                  placement: 'experience',
                  variations: ['fixed-bar', 'full-page-takeover']
                }}
              />

              {department &&
                department.acceptingRequests &&
                (myself ? (
                  <CategoryChatWidget categoryId={sellable.categories[0].id} />
                ) : (
                  <Experiment id="engagement-channel-experience" default="off">
                    {({ result }) => {
                      return (
                        <CategoryChatWidget
                          categoryId={sellable.categories[0].id}
                          enableTeaserChat={result === 'teaser-chat'}
                        />
                      )
                    }}
                  </Experiment>
                ))}

              <div className={styles.imageCarousel}>
                <BackButton label="Back" />
                <ImageCarousel images={sellable.images} />
              </div>
              {sellableCategory && <CallExpertBanner categorySlug={sellableCategory.slug} />}

              <Grid areas={defaultTopSection} areasMd={mdTopSection} className={styles.topSection}>
                {({
                  Title: TitleArea,
                  Features: FeaturesArea,
                  DescriptionHeader,
                  Description: DescriptionArea,
                  AmenitiesHeader,
                  Amenities: AmenitiesArea,
                  Rates,
                  RatesHeader,
                  Pricing: PricingArea
                }) => (
                  <>
                    <TitleArea>
                      <Title
                        title={sellable.title}
                        subTitle={
                          sellable.charterLocation &&
                          sellable.charterLocation.formatted &&
                          sellable.charterLocation.formatted.displayHtml
                        }
                      />
                    </TitleArea>

                    <FeaturesArea>
                      <Features sellable={sellable} />
                    </FeaturesArea>

                    {sellable.descriptionText && (
                      <>
                        <DescriptionHeader>
                          <SectionHeader header="About" />
                        </DescriptionHeader>

                        <DescriptionArea>
                          <Description descriptionText={sellable.descriptionText} />
                        </DescriptionArea>
                      </>
                    )}

                    {sellable.charterAmenities && (
                      <>
                        <AmenitiesHeader>
                          <SectionHeader header="Amenities" />
                        </AmenitiesHeader>

                        <AmenitiesArea>
                          <Amenities sellable={sellable} />
                        </AmenitiesArea>
                      </>
                    )}

                    <Waypoint
                      onLeave={this.handleScroll}
                      onEnter={this.handleScroll}
                      scrollableAncestor="window"
                    />

                    <RatesHeader>
                      <SectionHeader
                        header="Rates"
                        mobileSubHeader="Swipe to view more info"
                        withBorder
                      />
                    </RatesHeader>

                    <Rates>
                      <RatesTable sellable={sellable} />
                    </Rates>

                    <PricingArea className={styles.pricingContainer}>
                      <YachtPricing
                        categoryId={sellableCategory.id}
                        sellable={sellable}
                        categorySlug={sellableCategory.slug}
                        onVariationSelected={this.handleVariationSelected}
                      />
                    </PricingArea>
                  </>
                )}
              </Grid>

              {department &&
                department.acceptingRequests && (
                  <Expert
                    header={
                      <>
                        Make it an <em>unforgettable</em> experience
                      </>
                    }
                    body={
                      <>
                        From customized amenities to world class catering, our experts are ready to
                        plan your perfect day.
                      </>
                    }
                    cta="Connect with an expert"
                    categorySlug={sellableCategory.slug}
                    departmentType={DepartmentType.YACHT_CHARTERS}
                  />
                )}

              <Grid>
                <GridItem col={2} mdCol={2}>
                  <SectionHeader header="Specifications" />
                </GridItem>
                <GridItem col={2} mdCol={6}>
                  <Specifications specifications={sellable.specifications} />
                </GridItem>
              </Grid>

              <Grid>
                <GridItem col={2} mdCol={2}>
                  <SectionHeader header="Need to Know" withBorder />
                </GridItem>

                <GridItem col={2} mdCol={6}>
                  <FAQ />
                </GridItem>
              </Grid>

              {this.state.isLazyContentDisplayed && (
                <>
                  {sellable.serviceLocation &&
                    sellable.serviceLocation.displayTitle && (
                      <Grid>
                        <GridItem col={2} mdCol={2}>
                          <SectionHeader header="Location" withBorder />
                        </GridItem>
                        <GridItem col={2} mdCol={6}>
                          <Map location={sellable.serviceLocation.displayTitle} />
                        </GridItem>
                      </Grid>
                    )}

                  <SimilarExperiences sellableId={sellable.id} />
                </>
              )}
            </div>
            <Footer className={styles.footer} />
          </>
        )}
      </IdentityConsumer>
    )
  }
}

export default withRouter(withAnalytics(ExperiencePage))
